// apps/web/src/pages/HomePage.tsx
// Phase 5 — 首页 Dashboard

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@stores/authStore";
import { getStatsApi, type DashboardStats } from "@api/search";
import { normalizeEpochMs } from "@utils/time";

export default function HomePage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ["stats"],
    queryFn: getStatsApi,
    staleTime: 30_000, // 30s 缓存
    retry: 1,
  });

  const greeting = getGreeting();

  return (
    <div className="min-h-screen bg-surface pb-20">
      {/* 顶部渐变区 */}
      <div className="bg-gradient-to-br from-primary to-accent px-4 pt-safe pb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80">{greeting}</p>
            <h1 className="text-xl font-bold mt-0.5">
              {user?.nickname || user?.email?.split("@")[0] || "同学"}
            </h1>
          </div>
          <button
            onClick={() => navigate("/search")}
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm btn-tap"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-4 gap-2 mt-6">
          <StatCard label="题库" value={isLoading ? "—" : String(stats?.total_banks ?? 0)} />
          <StatCard label="题目" value={isLoading ? "—" : String(stats?.total_questions ?? 0)} />
          <StatCard label="错题" value={isLoading ? "—" : String(stats?.wrong_count ?? 0)} />
          <StatCard label="收藏" value={isLoading ? "—" : String(stats?.favorite_count ?? 0)} />
        </div>
      </div>

      {/* 快捷入口 */}
      <div className="px-4 -mt-4">
        <div className="grid grid-cols-4 gap-3 bg-white rounded-xl shadow-card p-4">
          <QuickAction
            icon={<BookIcon />}
            label="题库"
            to="/banks"
            color="text-blue-500"
            bg="bg-blue-50"
          />
          <QuickAction
            icon={<WrongIcon />}
            label="错题本"
            to="/wrong"
            color="text-red-500"
            bg="bg-red-50"
          />
          <QuickAction
            icon={<StarIcon />}
            label="收藏"
            to="/favorites"
            color="text-yellow-500"
            bg="bg-yellow-50"
          />
          <QuickAction
            icon={<NoteIcon />}
            label="笔记"
            to="/notes"
            color="text-green-500"
            bg="bg-green-50"
          />
        </div>
      </div>

      {/* 继续学习 */}
      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-gray-900">继续学习</h2>
          <Link to="/banks" className="text-xs text-gray-400">
            查看全部 →
          </Link>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-white rounded-xl shadow-card animate-pulse" />
            ))}
          </div>
        ) : stats && stats.recent_banks.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_banks.map((bank) => (
              <ContinueLearnCard key={bank.id} bank={bank} />
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
}

// ========== 组件 ==========

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs opacity-70 mt-0.5">{label}</p>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  to,
  color,
  bg,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  color: string;
  bg: string;
}) {
  return (
    <Link to={to} className="flex flex-col items-center gap-1.5 btn-tap">
      <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <span className="text-xs text-gray-600">{label}</span>
    </Link>
  );
}

function ContinueLearnCard({
  bank,
}: {
  bank: DashboardStats["recent_banks"][number];
}) {
  const lastStudied = bank.last_studied_at
    ? formatRelative(bank.last_studied_at)
    : "未学习";

  return (
    <Link
      to={`/banks/${bank.id}`}
      className="block bg-white rounded-xl shadow-card p-4 hover:shadow-card-hover transition-shadow btn-tap"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{bank.name}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
            <span>{bank.question_count} 题</span>
            <span>·</span>
            <span>{lastStudied}</span>
          </div>
        </div>
        {bank.tag && (
          <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium mr-2">
            {bank.tag}
          </span>
        )}
        <svg
          viewBox="0 0 24 24"
          className="w-5 h-5 text-gray-300 shrink-0"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl shadow-card p-8 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      </div>
      <p className="text-sm text-gray-500 mb-1">还没有题库</p>
      <p className="text-xs text-gray-400 mb-4">创建第一个题库开始刷题吧</p>
      <Link
        to="/banks/new"
        className="inline-block px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors btn-tap"
      >
        创建题库
      </Link>
    </div>
  );
}

// ========== 图标 ==========

function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function WrongIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

// ========== 工具函数 ==========

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 6) return "夜深了";
  if (h < 9) return "早上好";
  if (h < 12) return "上午好";
  if (h < 14) return "中午好";
  if (h < 18) return "下午好";
  return "晚上好";
}

function formatRelative(ts: number): string {
  const normalizedTs = normalizeEpochMs(ts);
  const diff = Date.now() - normalizedTs;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  const d = new Date(normalizedTs);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
